/**
 * RestoID shared presentation primitives.
 * ---------------------------------------------------------------------------
 * A faithful TSX port of the RestoID Claude Design (`custom/design-reference/app.js`):
 * the same colors, fonts, icons, fields, buttons and layout atoms — but as plain
 * presentation components that the custom pages compose around the PRISTINE
 * upstream Logto logic (hooks/containers). No upstream file is edited; nothing
 * here talks to the Experience API — flow logic stays in the upstream hooks.
 *
 * Styling is inline (exactly like the design source) so the visual is 1:1 and
 * immune to CSS-module hashing; theme tokens still resolve via CSS variables.
 */
import { type Nullable } from '@silverhand/essentials';
import { type CSSProperties, type ReactNode, useRef, useState } from 'react';

import { RESTOID_LOGO_SVG } from '../logo';

/* ----------------------------------------------------------------- colors */
/**
 * Every entry reads a `--rid-*` token declared in `custom/theme/restoid.css`,
 * with the current dark value inlined as the var() fallback so the components
 * still render correctly if the stylesheet has not applied yet.
 *
 * These used to be frozen literals, which meant the theme layer could restyle the
 * upstream screens but not RestoID's own — the palette was unreachable from CSS.
 * Going through tokens is what makes a theme swap possible at all; do NOT put a
 * raw color back here. The one exception below is the Russian flag, which is a
 * national flag, not a themeable surface.
 */
export const C = {
  brand: 'var(--rid-brand, #b0cf2f)',
  text: 'var(--rid-text, #f3f4f5)',
  sub: 'var(--rid-sub, rgba(243,244,245,0.54))',
  faint: 'var(--rid-faint, rgba(243,244,245,0.36))',
  line: 'var(--rid-line, rgba(255,255,255,0.10))',
  lineSoft: 'var(--rid-line-soft, rgba(255,255,255,0.06))',
  card: 'var(--rid-card, rgba(255,255,255,0.026))',
  cardHover: 'var(--rid-card-hover, rgba(255,255,255,0.05))',
  field: 'var(--rid-field, rgba(255,255,255,0.03))',
  tile: 'var(--rid-tile, rgba(176,207,47,0.10))',
  tileLine: 'var(--rid-tile-line, rgba(176,207,47,0.18))',
  ring: 'var(--rid-ring, rgba(176,207,47,0.13))',
  inset: 'var(--rid-inset, rgba(255,255,255,0.03))',
  insetFocus: 'var(--rid-inset-focus, rgba(255,255,255,0.04))',
  page: 'var(--rid-page, #0a0b0d)',
  danger: 'var(--rid-danger, #f1707a)',
  /* Danger's tile treatment — the error hero, in two weights. */
  dangerTile: 'var(--rid-danger-tile, rgba(241,112,122,0.10))',
  dangerTileLine: 'var(--rid-danger-tile-line, rgba(241,112,122,0.20))',
  dangerTileStrong: 'var(--rid-danger-tile-strong, rgba(241,112,122,0.12))',
  dangerTileLineStrong: 'var(--rid-danger-tile-line-strong, rgba(241,112,122,0.32))',
  /* The amber warning, in the same two weights as danger's tile. */
  warning: 'var(--rid-warning, #e8b55c)',
  warningTile: 'var(--rid-warning-tile, rgba(232,181,92,0.09))',
  warningTileLine: 'var(--rid-warning-tile-line, rgba(232,181,92,0.20))',
  ctaInk: 'var(--rid-cta-ink, #111315)',
} as const;

/**
 * Control geometry, in one place. `field` matches the CTA height on purpose: a 58px
 * field above a 54px button made the stack look top-heavy, and nothing about the
 * field needs the extra 4px.
 */
export const SIZE = {
  field: 'var(--rid-h-field, 54px)',
  cta: 'var(--rid-h-cta, 54px)',
} as const;

export const FONT =
  '"Inter", -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif';

/* ------------------------------------------------------------------ icons */
/* Exact Lucide icon source (raw inner SVG markup, rendered via innerHTML). */
const P: Record<string, string> = {
  back: '<path d="m15 18-6-6 6-6"/>',
  chevR: '<path d="m9 18 6-6-6-6"/>',
  lock: '<rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>',
  finger:
    '<path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4"/><path d="M14 13.12c0 2.38 0 6.38-1 8.88"/><path d="M17.29 21.02c.12-.6.43-2.3.5-3.02"/><path d="M2 12a10 10 0 0 1 18-6"/><path d="M2 16h.01"/><path d="M21.8 16c.2-2 .131-5.354 0-6"/><path d="M5 19.5C5.5 18 6 15 6 12a6 6 0 0 1 .34-2"/><path d="M8.65 22c.21-.66.45-1.32.57-2"/><path d="M9 6.8a6 6 0 0 1 9 5.2v2"/>',
  building:
    '<rect width="16" height="20" x="4" y="2" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/>',
  smartphone: '<rect width="14" height="20" x="5" y="2" rx="2" ry="2"/><path d="M12 18h.01"/>',
  eye: '<path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/><circle cx="12" cy="12" r="3"/>',
  eyeOff:
    '<path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"/><path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"/><path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"/><path d="m2 2 20 20"/>',
  clock: '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>',
  refresh: '<path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/>',
  edit: '<path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"/><path d="m15 5 4 4"/>',
  mail: '<rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>',
  user: '<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
  alert:
    '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
  check: '<path d="M20 6 9 17l-5-5"/>',
  x: '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>',
  key: '<path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 1 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z"/><circle cx="16.5" cy="7.5" r=".5" fill="currentColor"/>',
  copy: '<rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>',
  download:
    '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/>',
  swap: '<path d="m16 3 4 4-4 4"/><path d="M20 7H4"/><path d="m8 21-4-4 4-4"/><path d="M4 17h16"/>',
};

type IconProps = { name: string; size?: number; sw?: number; style?: CSSProperties };
export const Icon = ({ name, size = 22, sw = 1.8, style }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={sw}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
    // eslint-disable-next-line react/no-danger -- static, build-time icon markup
    dangerouslySetInnerHTML={{ __html: P[name] ?? '' }}
  />
);

export const Spin = ({
  size = 18,
  bw = 2,
  color = 'var(--rid-brand, #b0cf2f)',
  track = 'var(--rid-brand-track, rgba(176,207,47,0.25))',
}: {
  size?: number;
  bw?: number;
  color?: string;
  track?: string;
}) => (
  <span
    style={{
      display: 'inline-block',
      flex: '0 0 auto',
      width: size,
      height: size,
      borderRadius: '50%',
      border: `${bw}px solid ${track}`,
      borderTopColor: color,
      animation: 'restoidspin .8s linear infinite',
    }}
  />
);

/* --------------------------------------------------------- phone helpers */
/** National significant number (≤10 digits), tolerant of 8 / +7 / 7 prefixes. */
export const phoneNsn = (input: string): string => {
  let d = String(input).replace(/\D/g, '');
  if (d.startsWith('8') || d.startsWith('7')) {
    d = d.slice(1);
  }
  return d.slice(0, 10);
};

/** Visual RU mask: "+7 (911) 123-45-67". */
export const formatPhoneNat = (raw: string): string => {
  const n = phoneNsn(raw);
  if (!n) {
    return '';
  }
  let out = `+7 (${n.slice(0, 3)}`;
  if (n.length >= 3) {
    out += ')';
  }
  if (n.length > 3) {
    out += ` ${n.slice(3, 6)}`;
  }
  if (n.length > 6) {
    out += `-${n.slice(6, 8)}`;
  }
  if (n.length > 8) {
    out += `-${n.slice(8, 10)}`;
  }
  return out;
};

export const phoneComplete = (input: string): boolean => phoneNsn(input).length === 10;

/**
 * The value the upstream Experience API expects for a phone identifier:
 * country code + national number, digits only, NO leading "+"
 * (mirrors SmartInputField's `${countryCode}${inputValue}`).
 */
export const phoneToIdentifierValue = (input: string): string => `7${phoneNsn(input)}`;

/* ----------------------------------------------------- low level controls */
type PhoneInputProps = {
  value: string;
  onChange: (next: string) => void;
  danger?: boolean;
};
export const PhoneInput = ({ value, onChange, danger }: PhoneInputProps) => {
  const ref = useRef<HTMLInputElement>(null);
  const [isFocus, setIsFocus] = useState(false);
  const nat = value.includes('(') ? value.slice(value.indexOf('(')) : '';

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const prevDigits = phoneNsn(value);
    let nextDigits = phoneNsn(event.target.value);
    const { nativeEvent } = event;
    const inputType = nativeEvent instanceof InputEvent ? nativeEvent.inputType : undefined;
    // A backspace that only removed a formatting char (")", space, "-") leaves the
    // digit count unchanged — drop the digit behind it so the caret never sticks.
    if (
      inputType === 'deleteContentBackward' &&
      nextDigits.length === prevDigits.length &&
      nextDigits.length > 0
    ) {
      nextDigits = nextDigits.slice(0, -1);
    }
    onChange(formatPhoneNat(nextDigits));
  };

  return (
    // Clicking the field focuses the inner, keyboard-accessible <input>; the handler
    // adds nothing a keyboard user lacks (they tab straight to the input).
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={() => ref.current?.focus()}
      style={{
        display: 'flex',
        alignItems: 'center',
        height: SIZE.field,
        padding: '0 14px',
        cursor: 'text',
        background: C.field,
        borderRadius: 13,
        border: `1px solid ${danger ? C.danger : isFocus ? C.brand : C.line}`,
        boxShadow: isFocus
          ? `0 0 0 4px ${C.ring}, inset 0 1px 0 ${C.insetFocus}`
          : `inset 0 1px 0 ${C.inset}`,
        transition: 'border-color .15s ease, box-shadow .15s ease',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          paddingRight: 12,
          marginRight: 12,
          borderRight: `1px solid ${C.line}`,
        }}
      >
        <span
          style={{
            width: 21,
            height: 15,
            borderRadius: 3,
            overflow: 'hidden',
            display: 'grid',
            gridTemplateRows: '1fr 1fr 1fr',
            boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.3)',
          }}
        >
          <span style={{ background: '#fff' }} />
          <span style={{ background: '#1c3f97' }} />
          <span style={{ background: '#d52b1e' }} />
        </span>
        <span style={{ color: C.text, font: `600 15.5px/1 ${FONT}` }}>+7</span>
      </span>
      <input
        ref={ref}
        inputMode="tel"
        autoComplete="tel"
        placeholder="(___) ___-__-__"
        value={nat}
        onChange={handleChange}
        onFocus={() => setIsFocus(true)}
        onBlur={() => setIsFocus(false)}
        style={{
          flex: 1,
          minWidth: 0,
          height: '100%',
          border: 'none',
          outline: 'none',
          background: 'transparent',
          color: C.text,
          font: `500 16.5px/1 ${FONT}`,
          letterSpacing: '0.02em',
        }}
      />
    </div>
  );
};

type FieldProps = {
  value: string;
  onChange: (next: string) => void;
  label?: ReactNode;
  password?: boolean;
  placeholder?: string;
  inputType?: string;
  inputMode?: 'text' | 'email' | 'tel' | 'numeric';
  autoComplete?: string;
  danger?: boolean;
  errorMessage?: ReactNode;
  /* --- form-binding props --------------------------------------------------
   * Everything below simply forwards to the inner <input>. They exist so a
   * form library (react-hook-form, on the set-password screens) can drive the
   * field exactly as it drives a native input: `name` for the submitted /
   * password-manager identity, `onBlur` for blur-triggered re-validation,
   * `inputRef` so focus-the-first-invalid-field still works, `maxLength` for
   * the password policy's ceiling, `autoFocus` for the first field of a form.
   * All optional — a field that passes none behaves exactly as before. */
  name?: string;
  autoFocus?: boolean;
  maxLength?: number;
  onBlur?: () => void;
  inputRef?: (node: Nullable<HTMLInputElement>) => void;
};
export const Field = ({
  value,
  onChange,
  label,
  password,
  placeholder,
  inputType,
  inputMode,
  autoComplete,
  danger,
  errorMessage,
  name,
  autoFocus,
  maxLength,
  onBlur,
  inputRef,
}: FieldProps) => {
  const ref = useRef<Nullable<HTMLInputElement>>(null);
  const [isFocus, setIsFocus] = useState(false);
  const [show, setShow] = useState(false);

  return (
    <div>
      {label ? (
        <label
          style={{ display: 'block', color: C.sub, font: `500 13px/1 ${FONT}`, marginBottom: 9 }}
        >
          {label}
        </label>
      ) : null}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        onClick={() => ref.current?.focus()}
        style={{
          display: 'flex',
          alignItems: 'center',
          height: SIZE.field,
          padding: '0 14px 0 16px',
          cursor: 'text',
          background: C.field,
          borderRadius: 13,
          border: `1px solid ${danger ? C.danger : isFocus ? C.brand : C.line}`,
          boxShadow: isFocus
            ? `0 0 0 4px ${C.ring}, inset 0 1px 0 ${C.insetFocus}`
            : `inset 0 1px 0 ${C.inset}`,
          transition: 'border-color .15s ease, box-shadow .15s ease',
        }}
      >
        <input
          ref={(node) => {
            ref.current = node;
            inputRef?.(node);
          }}
          name={name}
          type={password ? (show ? 'text' : 'password') : (inputType ?? 'text')}
          inputMode={inputMode}
          autoComplete={autoComplete ?? (password ? 'current-password' : undefined)}
          autoFocus={autoFocus}
          maxLength={maxLength}
          aria-invalid={danger}
          placeholder={placeholder ?? ''}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onFocus={() => setIsFocus(true)}
          onBlur={() => {
            setIsFocus(false);
            onBlur?.();
          }}
          style={{
            flex: 1,
            minWidth: 0,
            height: '100%',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: C.text,
            font: `500 16px/1 ${FONT}`,
            letterSpacing: '0.01em',
          }}
        />
        {password ? (
          <button
            type="button"
            tabIndex={-1}
            aria-label="toggle"
            onClick={() => setShow((value_) => !value_)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: 6,
              marginLeft: 6,
              border: 'none',
              background: 'none',
              color: C.faint,
              cursor: 'pointer',
              transition: 'color .15s ease',
            }}
          >
            <Icon name={show ? 'eyeOff' : 'eye'} size={18} />
          </button>
        ) : null}
      </div>
      {danger && errorMessage ? (
        // Upstream's ErrorMessage carries role="alert", and screens that were ported
        // off it (every /continue, /device and MFA form) lost the announcement when
        // this div replaced it — the text was visible but silent to a screen reader.
        <div
          role="alert"
          style={{ marginTop: 8, color: C.danger, font: `400 12.5px/1.45 ${FONT}` }}
        >
          {errorMessage}
        </div>
      ) : null}
    </div>
  );
};

/* ------------------------------------------------------------- the CTA button */
type ButtonProps = {
  title: ReactNode;
  onClick?: React.MouseEventHandler;
  htmlType?: 'button' | 'submit';
  isLoading?: boolean;
  isDisabled?: boolean;
};
export const Button = ({
  title,
  onClick,
  htmlType = 'button',
  isLoading,
  isDisabled,
}: ButtonProps) => {
  const disabled = Boolean(isDisabled) || Boolean(isLoading);
  // Idle-disabled (not mid-submit): a clearly muted, flat fill — no lime, no
  // depth — so it reads as inactive. While loading it stays the active lime.
  const isOff = Boolean(isDisabled) && !isLoading;
  return (
    // Everything visual (fill, ink, depth, type, height, radius, the sheen and
    // both transitions) lives in ONE place: the `.restoid-cta` block in
    // custom/theme/restoid.css, shared with the upstream primary buttons. This
    // used to be a second, hand-synced copy of those declarations and the two had
    // already drifted. Only layout and state belong here now.
    <button
      type={htmlType}
      className={isOff ? 'restoid-cta restoid-cta--off' : 'restoid-cta'}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        border: 'none',
      }}
    >
      {/* Dark spinner to match the CTA text — a lime spinner on the lime fill has no contrast. */}
      {isLoading ? (
        <Spin
          size={18}
          bw={2}
          color={C.ctaInk}
          track="var(--rid-cta-ink-track, rgba(17,19,21,0.22))"
        />
      ) : null}
      <span>{title}</span>
    </button>
  );
};

/* --------------------------------------------------------------- text bits */
export const LinkText = ({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick?: React.MouseEventHandler;
}) => {
  const [hov, setHov] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        color: C.brand,
        font: `500 14px/1.4 ${FONT}`,
        textDecoration: hov ? 'underline' : 'none',
      }}
    >
      {children}
    </button>
  );
};

/** Premium centered secondary action (skip / all options / link another). */
export const GhostAction = ({
  children,
  onClick,
  icon,
}: {
  children: ReactNode;
  onClick?: React.MouseEventHandler;
  icon?: string;
}) => {
  const [hov, setHov] = useState(false);
  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <button
        type="button"
        onClick={onClick}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          padding: '9px 16px',
          borderRadius: 10,
          background: hov ? C.cardHover : 'transparent',
          border: `1px solid ${hov ? C.line : C.lineSoft}`,
          color: hov ? C.text : C.sub,
          cursor: 'pointer',
          font: `500 13px/1 ${FONT}`,
          transition: 'background .15s ease, border-color .15s ease, color .15s ease',
        }}
      >
        {icon ? <Icon name={icon} size={15} /> : null}
        <span>{children}</span>
      </button>
    </div>
  );
};

/** A tappable method / option row used across the method-picker screens. */
export const OptionRow = ({
  icon,
  title,
  desc,
  onClick,
}: {
  icon: string;
  title: ReactNode;
  desc: ReactNode;
  onClick?: React.MouseEventHandler;
}) => {
  const [hov, setHov] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        width: '100%',
        textAlign: 'left',
        padding: '14px 16px',
        borderRadius: 12,
        cursor: 'pointer',
        background: hov ? C.cardHover : C.card,
        border: `1px solid ${hov ? C.line : C.lineSoft}`,
        transition: 'all .15s ease',
      }}
    >
      <span
        style={{
          flex: '0 0 auto',
          width: 40,
          height: 40,
          borderRadius: 10,
          background: C.tile,
          display: 'grid',
          placeItems: 'center',
          color: C.brand,
          border: `1px solid ${C.tileLine}`,
        }}
      >
        <Icon name={icon} size={20} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', color: C.text, font: `600 15px/1.3 ${FONT}` }}>
          {title}
        </span>
        <span
          style={{ display: 'block', color: C.sub, font: `400 13px/1.4 ${FONT}`, marginTop: 2 }}
        >
          {desc}
        </span>
      </span>
      <span style={{ color: C.faint }}>
        <Icon name="chevR" size={18} />
      </span>
    </button>
  );
};

type ScreenHeadProps = {
  title: ReactNode;
  sub?: ReactNode;
  back?: () => void;
  backLabel?: ReactNode;
};
export const ScreenHead = ({ title, sub, back, backLabel }: ScreenHeadProps) => (
  <div style={{ marginBottom: 22 }}>
    {back ? (
      <button
        type="button"
        onClick={back}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 18,
          height: 34,
          padding: '0 13px 0 9px',
          borderRadius: 10,
          background: C.card,
          border: `1px solid ${C.lineSoft}`,
          cursor: 'pointer',
          color: C.sub,
          font: `500 13px/1 ${FONT}`,
          transition: 'background .15s ease, border-color .15s ease, color .15s ease',
        }}
      >
        <Icon name="back" size={15} />
        <span>{backLabel}</span>
      </button>
    ) : null}
    <div style={{ textAlign: 'center' }}>
      <h2
        style={{
          margin: 0,
          color: C.text,
          fontFamily: FONT,
          fontWeight: 600,
          fontSize: 23,
          lineHeight: 1.22,
          letterSpacing: '-0.02em',
        }}
      >
        {title}
      </h2>
      {sub ? (
        <p
          style={{
            margin: '9px auto 0',
            color: C.sub,
            font: `400 14px/1.55 ${FONT}`,
            maxWidth: 300,
          }}
        >
          {sub}
        </p>
      ) : null}
    </div>
  </div>
);

export const Stack = ({ children, gap = 16 }: { children: ReactNode; gap?: number }) => (
  <div className="restoid-reveal" style={{ display: 'flex', flexDirection: 'column', gap }}>
    {children}
  </div>
);

/**
 * Centered brand lockup used as the sign-in card's head — the RestoID logo in
 * place of a text title, with an optional subtitle (1:1 with the design's BrandHead).
 */
export const BrandHead = ({ sub }: { sub?: ReactNode }) => (
  <div style={{ marginBottom: 22, textAlign: 'center' }}>
    <span
      style={{ display: 'inline-block', height: 44 }}
      // eslint-disable-next-line react/no-danger -- static, build-time logo markup
      dangerouslySetInnerHTML={{ __html: RESTOID_LOGO_SVG }}
    />
    {sub ? (
      <p
        style={{
          margin: '16px auto 0',
          color: C.sub,
          font: `400 14px/1.55 ${FONT}`,
          maxWidth: 300,
        }}
      >
        {sub}
      </p>
    ) : null}
  </div>
);

/** Centered lime hero tile (used by passkey / MFA / success heroes). */
export const HeroTile = ({
  icon,
  size = 72,
  iconSize = 34,
}: {
  icon: string;
  size?: number;
  iconSize?: number;
}) => (
  <div style={{ display: 'flex', justifyContent: 'center', margin: '4px 0 2px' }}>
    <span
      style={{
        width: size,
        height: size,
        borderRadius: 20,
        background: C.tile,
        border: `1px solid ${C.tileLine}`,
        display: 'grid',
        placeItems: 'center',
        color: C.brand,
      }}
    >
      <Icon name={icon} size={iconSize} sw={1.6} />
    </span>
  </div>
);

export const Divider = ({ label }: { label?: ReactNode }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    <span style={{ flex: 1, height: 1, background: C.line }} />
    {label ? <span style={{ color: C.faint, font: `400 12px/1 ${FONT}` }}>{label}</span> : null}
    <span style={{ flex: 1, height: 1, background: C.line }} />
  </div>
);
